import React, { useState, useEffect } from "react";
import { Col, Row, Card, Button, Spinner, Alert, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faChartBar, faCrown, faCalendarDay, faCalendarWeek, faCalendarAlt, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';

export default () => {
    const [stats, setStats] = useState({
        totalSold: 0,
        totalRevenue: 0,
        packageStats: {},
        dailyStats: {},
        lastUpdate: null
    });
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            const response = await api.get('/statistics/vip-sales').catch(() => ({ data: null }));
            const data = response.data;

            if (data && data.totalSold !== undefined) {
                setStats(data);
            } else {
                // Initialize empty stats
                setStats({
                    totalSold: 0,
                    totalRevenue: 0,
                    packageStats: {},
                    dailyStats: {},
                    lastUpdate: null
                });
            }
        } catch (err) {
            console.error("Error loading VIP stats:", err);
            setError("İstatistikler yüklenirken hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const updateStats = async () => {
        try {
            setUpdating(true);
            setError(null);
            setSuccess(null);

            // Fetch all payments
            const response = await api.get('/billing/vip-payments?page=1&limit=5000');
            const payload = response.data || {};
            const payments = Array.isArray(payload)
                ? payload
                : (payload.payments || payload.data || payload.purchases || []);
            
            // In a real scenario we'd query by date from API. For now filter manually:
            let newPayments = payments;
            if (stats.lastUpdate) {
                const lastUpDate = new Date(stats.lastUpdate);
                newPayments = payments.filter(p => new Date(p.createdAt || p.date || 0) > lastUpDate);
            }

            if (newPayments.length === 0) {
                setSuccess("Yeni veri bulunamadı. İstatistikleriniz zaten güncel.");
                setUpdating(false);
                return;
            }

            // Clone current stats
            const newStats = {
                totalSold: stats.totalSold || 0,
                totalRevenue: stats.totalRevenue || 0,
                packageStats: { ...(stats.packageStats || {}) },
                dailyStats: { ...(stats.dailyStats || {}) }
            };

            let newSalesCount = 0;
            let newRevenueCount = 0;
            let latestDate = stats.lastUpdate ? new Date(stats.lastUpdate) : new Date(0);

            newPayments.forEach(payment => {

                // Only count successful payments
                if (payment.status !== "Başarılı Alım" && payment.status !== "Başarılı Ödeme") {
                    return;
                }

                const price = parseFloat(payment.price) || 0;
                const sku = payment.sku || "Bilinmeyen Paket";
                const date = new Date(payment.createdAt || payment.date || 0);

                // Invalid date handled
                if (isNaN(date.getTime())) return;

                // Update latest date tracking for lastUpdate
                if (date > latestDate) {
                    latestDate = date;
                }

                // Add to totals
                newStats.totalSold += 1;
                newStats.totalRevenue += price;
                newSalesCount += 1;
                newRevenueCount += price;

                // Add to package stats
                newStats.packageStats[sku] = (newStats.packageStats[sku] || 0) + 1;

                // Add to daily stats (Format: YYYY-MM-DD)
                const dayKey = date.toISOString().split('T')[0];
                newStats.dailyStats[dayKey] = (newStats.dailyStats[dayKey] || 0) + 1;
            });

            // Update Firestore
            // Set lastUpdate to server timestamp or the current Date object
            newStats.lastUpdate = new Date(); // Use local current time for update mark

            await api.post('/statistics/vip-sales', newStats).catch(e => console.error(e));

            setStats(newStats);
            setSuccess(`Başarıyla güncellendi! ${newSalesCount} yeni satış eklendi (${newRevenueCount.toFixed(2)}₺).`);

        } catch (err) {
            console.error("Error updating stats:", err);
            setError("Veriler güncellenirken hata oluştu: " + err.message);
        } finally {
            setUpdating(false);
        }
    };

    // Helper to calculate sales in the last N days
    const getSalesLastNDays = (days) => {
        if (!stats.dailyStats) return 0;

        let total = 0;
        const now = new Date();

        // Check everyday in the last N days
        for (let i = 0; i < days; i++) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            const dayKey = d.toISOString().split('T')[0];

            if (stats.dailyStats[dayKey]) {
                total += stats.dailyStats[dayKey];
            }
        }

        return total;
    };

    // Find most sold package
    const getMostSoldPackage = () => {
        if (!stats.packageStats || Object.keys(stats.packageStats).length === 0) return "Henüz Veri Yok";

        let maxName = "";
        let maxVal = -1;

        for (const [sku, count] of Object.entries(stats.packageStats)) {
            if (count > maxVal) {
                maxVal = count;
                maxName = sku;
            }
        }

        return `${maxName} (${maxVal} Adet)`;
    };

    const formatDate = (dateObj) => {
        if (!dateObj) return "Hiç güncellenmedi";
        try {
            const d = new Date(dateObj);
            if (isNaN(d.getTime())) return "Hiç güncellenmedi";
            return d.toLocaleString('tr-TR');
        } catch {
            return "Hiç güncellenmedi";
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="info" />
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h4>VIP Satış İstatistikleri</h4>
                    <p className="mb-0 text-muted">
                        Son Güncelleme: <span className="fw-bold">{formatDate(stats.lastUpdate)}</span>
                    </p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Button variant="info" onClick={updateStats} disabled={updating} className="d-flex align-items-center">
                        {updating ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                            <FontAwesomeIcon icon={faSync} className="me-2" />
                        )}
                        {updating ? "Güncelleniyor..." : "Verileri Güncelle"}
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* Main Metrics */}
            <Row className="mb-4">
                <Col xs={12} sm={6} xl={4} className="mb-4">
                    <Card className="border-0 shadow-sm" style={{ backgroundColor: '#fff3cd' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="icon-shape icon-shape-warning rounded me-4 pe-4">
                                <FontAwesomeIcon icon={faCrown} className="fs-1 text-warning" />
                            </div>
                            <div className="d-block">
                                <h6 className="fw-normal text-muted mb-1">En Çok Satılan Paket (Tüm Zamanlar)</h6>
                                <h4 className="fw-bold mb-0 text-dark">{getMostSoldPackage()}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={4} className="mb-4">
                    <Card className="border-0 shadow-sm" style={{ backgroundColor: '#d1ecf1' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="icon-shape icon-shape-info rounded me-4 pe-4">
                                <FontAwesomeIcon icon={faChartBar} className="fs-1 text-info" />
                            </div>
                            <div className="d-block">
                                <h6 className="fw-normal text-muted mb-1">Toplam VIP Paket Satışı</h6>
                                <h4 className="fw-bold mb-0 text-dark">{stats.totalSold} Adet</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={4} className="mb-4">
                    <Card className="border-0 shadow-sm" style={{ backgroundColor: '#d4edda' }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="icon-shape icon-shape-success rounded me-4 pe-4">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="fs-1 text-success" />
                            </div>
                            <div className="d-block">
                                <h6 className="fw-normal text-muted mb-1">Toplam Gelir (Tüm Zamanlar)</h6>
                                <h4 className="fw-bold mb-0 text-dark">{parseFloat(stats.totalRevenue).toFixed(2)}₺</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Time Based Metrics */}
            <Row className="mb-4">
                <Col xs={12} sm={4} className="mb-4">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faCalendarDay} size="2x" className="text-primary mb-3" />
                            <h5 className="fw-normal text-muted mb-2">Son 1 Günde Satılan</h5>
                            <h3 className="fw-bold mb-0">{getSalesLastNDays(1)} Adet</h3>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={4} className="mb-4">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faCalendarWeek} size="2x" className="text-primary mb-3" />
                            <h5 className="fw-normal text-muted mb-2">Son 7 Günde Satılan</h5>
                            <h3 className="fw-bold mb-0">{getSalesLastNDays(7)} Adet</h3>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={4} className="mb-4">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-primary mb-3" />
                            <h5 className="fw-normal text-muted mb-2">Son 30 Günde Satılan</h5>
                            <h3 className="fw-bold mb-0">{getSalesLastNDays(30)} Adet</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Package Breakdown Table */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-dark text-white">
                    <h5 className="mb-0">Paket Satış Dağılımı</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive className="table-centered table-nowrap rounded mb-0 text-dark">
                        <thead className="thead-light">
                            <tr>
                                <th className="border-0">Paket (SKU)</th>
                                <th className="border-0 text-end">Toplam Satış Sayısı</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!stats.packageStats || Object.keys(stats.packageStats).length === 0 ? (
                                <tr>
                                    <td colSpan="2" className="text-center py-4">Henüz veri yok. Güncelleyiniz.</td>
                                </tr>
                            ) : (
                                Object.entries(stats.packageStats)
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([sku, count]) => (
                                        <tr key={sku}>
                                            <td className="fw-bold">{sku}</td>
                                            <td className="text-end">{count}</td>
                                        </tr>
                                    ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    );
};
